"use client";

import { useComposedRefs } from "radix-ui/internal";
import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import cx from "clsx";
import * as React from "react";
import { useDebouncedCallback } from "use-debounce";
import { IconButton, TextField, TextFieldSlot } from "./elements.js";
import { useSearchContext } from "./search-provider.js";
import { useUsersManagementContext } from "./users-management-context.js";
import { namespaceClassNames } from "./utils.js";
import { useTranslation } from "./i18n/use-translation.js";

type UsersSearchProps = React.ComponentPropsWithoutRef<typeof TextField>;

export const UsersSearch = React.forwardRef<HTMLInputElement, UsersSearchProps>(
  ({ className, ...props }, ref) => {
    const { inputRef, clearSearch, searchValue, setSearchValue } =
      useSearchContext();
    const { dispatch } = useUsersManagementContext();
    const translate = useTranslation();

    const filter = useDebouncedCallback((value) => {
      dispatch({ type: "FILTER_BY_SEARCH", searchQuery: value });
    }, 200);

    const resetSearch = () => {
      clearSearch();
      filter.cancel();
    };

    return (
      <TextField
        ref={useComposedRefs(inputRef, ref)}
        className={cx(namespaceClassNames("users-search"), className)}
        autoComplete="off"
        aria-label={translate({
          defaultMessage: "User search",
          id: "lzPOKv",
          description: "Label for the user search input field",
        })}
        placeholder={translate({
          defaultMessage: "Search by name or e-mail",
          id: "VH4IKy",
          description: "Placeholder text for the user search input field",
        })}
        value={searchValue}
        onChange={(event) => {
          const value = event.target.value;
          setSearchValue(value);
          filter(value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            resetSearch();
          }
        }}
        {...props}
      >
        <TextFieldSlot side="left">
          <MagnifyingGlassIcon aria-hidden="true" height="16" width="16" />
        </TextFieldSlot>

        <TextFieldSlot side="right">
          {!!searchValue && (
            <IconButton
              size="1"
              onClick={resetSearch}
              title={translate({
                defaultMessage: "Clear search",
                id: "IBozK6",
                description: "Tooltip text for the clear search button",
              })}
            >
              <Cross2Icon aria-hidden="true" />
            </IconButton>
          )}
        </TextFieldSlot>
      </TextField>
    );
  },
);
